import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Loader2, Pencil, RotateCcw, X } from 'lucide-react';
import { vatReportingApi } from '../../services/vatReportingApi';
import { parseBankAmount } from '../../utils/bankAmountParse';
import { useVatLang } from './VatLang';
import type {
    AmountOverrideField, AmountOverrideHistoryResponse, VatCompany, VatEntryRow,
} from '../../types/vatReporting';

/**
 * Manual EUR correction of one posting (migration 229) - the cell that shows it and the dialog
 * that makes, changes or withdraws it.
 *
 * THE CASE. A document booked in CHF where BC converted to EUR with its own rate instead of the
 * document's. Heike types the figure off the original document; the return files that figure
 * instead of BC's, in every declaration at once. Rare by design - the cell does not invite an
 * edit, it offers one on hover.
 *
 * WHAT THE DIALOG SHOWS BEFORE IT ASKS: BC's figure, the figure in use, how far the new one is
 * from BC, and whether this column is the one the posting's Kennzahl reads at all. A purchase
 * line files its VAT amount; correcting its base changes the ZM base but not the UStVA.
 *
 * A REASON IS REQUIRED for a correction and for a reset (Roman, 2026-10-20). The history stays
 * when the correction is withdrawn.
 */

const money = (v: number | null | undefined) =>
    v === null || v === undefined
        ? '—'
        : new Intl.NumberFormat('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

const when = (iso: string) => iso
    ? new Date(iso).toLocaleString('de-CH', { dateStyle: 'short', timeStyle: 'short' })
    : '';

const periodName = (y: number, m: number) => `${y}-${String(m).padStart(2, '0')}`;

const valueOf = (row: VatEntryRow, field: AmountOverrideField) =>
    field === 'base' ? row.baseReporting : row.taxReporting;

const bcOf = (row: VatEntryRow, field: AmountOverrideField) =>
    (field === 'base' ? row.bcBaseReporting : row.bcTaxReporting) ?? null;

/** BC has moved since the correction was made - someone re-booked or reversed the document. */
export function overrideDrift(row: VatEntryRow, field: AmountOverrideField): boolean {
    const m = row.amountOverrides?.[field];
    if (!m || m.bcAmount === null || m.bcAmountAtSet === null) return false;
    return Math.abs(m.bcAmount - m.bcAmountAtSet) >= 0.005;
}

/**
 * The euro cell. Corrected: tinted, with the correction's reason and BC's figure in the tooltip,
 * and clickable for the history (also for a viewer). Editable and not corrected: a pencil on
 * hover. Otherwise the plain figure.
 *
 * inline-flex on every branch, for the reason ExclusionActionCell gives: a struck-through
 * (excluded) row would otherwise strike the button too.
 */
export function ReportingAmountCell({
    row, field, editable, onOpen,
}: {
    row: VatEntryRow;
    field: AmountOverrideField;
    /** May change it: edit right, open period, a BC figure to correct. */
    editable: boolean;
    onOpen?: (row: VatEntryRow, field: AmountOverrideField) => void;
}) {
    const { t } = useVatLang();
    const value = valueOf(row, field);
    const mark = row.amountOverrides?.[field] ?? null;
    const drift = overrideDrift(row, field);

    if (mark) {
        const title = [
            t('ovr.cellCorrected'),
            t('ovr.cellBc', { bc: money(mark.bcAmount) }),
            drift ? t('ovr.cellDrift', { then: money(mark.bcAmountAtSet), now: money(mark.bcAmount) }) : null,
            mark.comment,
            t('ovr.cellBy', { who: mark.updatedBy, when: when(mark.updatedAt) }),
        ].filter(Boolean).join(' · ');
        return (
            <button type="button" onClick={() => onOpen?.(row, field)} title={title}
                    disabled={!onOpen}
                    className="inline-flex items-center justify-end gap-1 w-full rounded px-1 -mx-1 bg-amber-100 text-amber-900 hover:bg-amber-200 no-underline">
                {drift
                    ? <AlertTriangle className="h-3 w-3 text-red-600 shrink-0" />
                    : <Pencil className="h-3 w-3 shrink-0" />}
                <span className="tabular-nums">{money(value)}</span>
            </button>
        );
    }

    if (editable && onOpen && bcOf(row, field) !== null) {
        return (
            <button type="button" onClick={() => onOpen(row, field)}
                    title={t('ovr.cellEditHint')}
                    className="group inline-flex items-center justify-end gap-1 w-full rounded px-1 -mx-1 hover:bg-blue-50 no-underline">
                <Pencil className="h-3 w-3 shrink-0 text-blue-500 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100" />
                <span className="tabular-nums">{money(value)}</span>
            </button>
        );
    }

    return <span className="inline-block tabular-nums">{money(value)}</span>;
}

interface Props {
    company: VatCompany;
    /** The month on screen - the server checks the posting against it. */
    year: number;
    month: number;
    row: VatEntryRow;
    field: AmountOverrideField;
    /** False: history only (no edit right, or the period is filed). */
    editable: boolean;
    onClose: () => void;
    onDone: () => void;
}

export function AmountOverrideDialog({
    company, year, month, row, field, editable, onClose, onDone,
}: Props) {
    const { t } = useVatLang();
    const mark = row.amountOverrides?.[field] ?? null;
    const bc = bcOf(row, field);
    const current = valueOf(row, field);

    const [raw, setRaw] = useState(current === null ? '' : current.toFixed(2));
    const [comment, setComment] = useState('');
    const [saving, setSaving] = useState<'set' | 'reset' | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [history, setHistory] = useState<AmountOverrideHistoryResponse | null>(null);
    const [historyError, setHistoryError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        vatReportingApi.getAmountOverrideHistory({ company, entryNo: row.entryNo })
            .then(h => !cancelled && setHistory(h))
            .catch(e => !cancelled && setHistoryError(e instanceof Error ? e.message : t('ovr.historyFailed')));
        return () => { cancelled = true; };
    }, [company, row.entryNo]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, saving]);

    const parsed = raw.trim() ? parseBankAmount(raw) : null;
    const signFlip = parsed !== null && bc !== null && bc !== 0 && parsed !== 0
        && Math.sign(parsed) !== Math.sign(bc);
    const unchanged = parsed !== null && current !== null && Math.abs(parsed - current) < 0.005;
    const deviationPct = parsed !== null && bc !== null && bc !== 0
        ? ((parsed - bc) / Math.abs(bc)) * 100 : null;
    const hasComment = comment.trim().length > 0;
    const canSet = editable && !saving && parsed !== null && !signFlip && !unchanged && hasComment;
    const canReset = editable && !saving && !!mark && hasComment;

    const fieldLabel = t(field === 'base' ? 'drill.baseReporting' : 'drill.taxReporting');
    // The posting files the other column (or nothing): say so before the change is made.
    const notFiled = row.reportingField !== undefined && row.reportingField !== field;

    const run = async (action: 'set' | 'reset') => {
        if (action === 'set' ? !canSet : !canReset) return;
        setSaving(action);
        setError(null);
        try {
            const base = { company, year, month, entryNo: row.entryNo, field, comment: comment.trim() };
            if (action === 'set') await vatReportingApi.setAmountOverride({ ...base, amount: parsed! });
            else await vatReportingApi.resetAmountOverride(base);
            onDone();
        } catch (e) {
            // Inside the dialog, so a refusal (filed period, sign) does not lose what was typed.
            setError(e instanceof Error ? e.message : t('ovr.failed'));
            setSaving(null);
        }
    };

    const audit = (history?.audit ?? []).filter(a => a.field === field);

    return createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
             onClick={() => !saving && onClose()}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col"
                 onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between px-5 py-3 border-b">
                    <div>
                        <h2 className="text-base font-semibold text-gray-900">
                            {t('ovr.title', { field: fieldLabel })}
                        </h2>
                        <p className="text-sm text-gray-500 mt-0.5">
                            {row.documentNo} · {t('drill.entry')} {row.entryNo}
                            {row.counterpartyName ? ` · ${row.counterpartyName}` : ''}
                        </p>
                    </div>
                    <button onClick={onClose} disabled={!!saving}
                            className="text-gray-400 hover:text-gray-600 p-1 disabled:opacity-40">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="px-5 py-4 space-y-3 overflow-auto">
                    <div className="rounded border bg-gray-50 px-3 py-2 text-sm space-y-1">
                        <div className="flex justify-between gap-4">
                            <span className="text-gray-500">{t('ovr.local', { field: t(field === 'base' ? 'drill.baseLocal' : 'drill.taxLocal') })}</span>
                            <span className="tabular-nums">{money(field === 'base' ? row.baseLocal : row.taxLocal)}</span>
                        </div>
                        <div className="flex justify-between gap-4">
                            <span className="text-gray-500">{t('ovr.bcValue')}</span>
                            <span className="tabular-nums">{money(bc)}</span>
                        </div>
                        <div className="flex justify-between gap-4">
                            <span className="text-gray-500">{t('ovr.inUse')}</span>
                            <span className={`font-medium tabular-nums ${mark ? 'text-amber-800' : ''}`}>
                                {money(current)}{mark ? ` (${t('ovr.manual')})` : ''}
                            </span>
                        </div>
                        {mark && overrideDrift(row, field) && (
                            <div className="flex items-start gap-1.5 text-xs text-red-700 pt-1">
                                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                {t('ovr.driftLong', { then: money(mark.bcAmountAtSet), now: money(mark.bcAmount) })}
                            </div>
                        )}
                    </div>

                    {notFiled && (
                        <p className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            {row.reportingField
                                ? t('ovr.otherColumn', {
                                    kz: row.kennzahl ?? '—',
                                    other: t(row.reportingField === 'base' ? 'drill.baseReporting' : 'drill.taxReporting'),
                                })
                                : t('ovr.noKennzahl')}
                        </p>
                    )}

                    {editable ? (
                        <>
                            <label className="block">
                                <span className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('ovr.newValue', { field: fieldLabel })}
                                </span>
                                <input value={raw} onChange={e => setRaw(e.target.value)}
                                       inputMode="decimal" autoFocus
                                       className="w-full border rounded px-2 py-1.5 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                <span className="block text-xs mt-0.5 text-gray-500">
                                    {raw.trim() && parsed === null && <span className="text-red-600">{t('ovr.notANumber')}</span>}
                                    {parsed !== null && signFlip && <span className="text-red-600">{t('ovr.signFlip')}</span>}
                                    {parsed !== null && !signFlip && deviationPct !== null && t('ovr.deviation', {
                                        amount: money(parsed - (bc ?? 0)),
                                        pct: deviationPct.toLocaleString('de-CH', { maximumFractionDigits: 2, signDisplay: 'always' }),
                                    })}
                                </span>
                            </label>

                            <label className="block">
                                <span className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('ovr.commentLabel')}<span className="text-red-600"> *</span>
                                </span>
                                <textarea value={comment}
                                          onChange={e => setComment(e.target.value.slice(0, 1000))}
                                          rows={2}
                                          placeholder={t('ovr.commentPlaceholder')}
                                          className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            </label>

                            <p className="flex items-start gap-2 text-xs text-gray-500">
                                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                {t('ovr.package')}
                            </p>
                        </>
                    ) : (
                        <p className="text-xs text-gray-500">{t('ovr.readOnly')}</p>
                    )}

                    {error && (
                        <div className="border border-red-200 bg-red-50 rounded px-3 py-2 text-sm text-red-800">
                            {error}
                        </div>
                    )}

                    <div>
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                            {t('ovr.history')}
                        </h3>
                        {historyError && <p className="text-xs text-red-600">{historyError}</p>}
                        {!history && !historyError && <p className="text-xs text-gray-400">{t('drill.loading')}</p>}
                        {history && audit.length === 0 && <p className="text-xs text-gray-400">{t('ovr.historyEmpty')}</p>}
                        {audit.length > 0 && (
                            <ul className="divide-y border rounded text-xs">
                                {audit.map(a => (
                                    <li key={a.id} className="px-2.5 py-1.5 space-y-0.5">
                                        <div className="flex justify-between gap-3">
                                            <span className="font-medium">
                                                {a.action === 'set'
                                                    ? t('ovr.historySet', { from: money(a.beforeAmount ?? a.bcAmount), to: money(a.afterAmount) })
                                                    : t('ovr.historyReset', { from: money(a.beforeAmount), to: money(a.bcAmount) })}
                                            </span>
                                            <span className="text-gray-400 whitespace-nowrap">{when(a.changedAt)}</span>
                                        </div>
                                        <div className="text-gray-600">{a.comment}</div>
                                        <div className="text-gray-400">
                                            {a.actor} · {t('ovr.historyBc', { bc: money(a.bcAmount) })} · {periodName(a.periodYear, a.periodMonth)}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                <div className="flex items-center justify-between gap-2 px-5 py-3 border-t bg-gray-50">
                    <div>
                        {editable && mark && (
                            <button onClick={() => run('reset')} disabled={!canReset}
                                    title={hasComment ? undefined : t('ovr.commentFirst')}
                                    className="px-3 py-1.5 text-sm border rounded bg-white hover:bg-gray-50 inline-flex items-center gap-1.5 disabled:opacity-40">
                                {saving === 'reset'
                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    : <RotateCcw className="h-3.5 w-3.5" />}
                                {t('ovr.reset', { bc: money(bc) })}
                            </button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button onClick={onClose} disabled={!!saving}
                                className="px-3 py-1.5 text-sm border rounded bg-white hover:bg-gray-50 disabled:opacity-40">
                            {editable ? t('excl.cancel') : t('ovr.close')}
                        </button>
                        {editable && (
                            <button onClick={() => run('set')} disabled={!canSet}
                                    className="px-3 py-1.5 text-sm rounded text-white inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40">
                                {saving === 'set' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                {t('ovr.save')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
